import { motion, AnimatePresence } from "framer-motion";
import { SlidersHorizontal, ChevronDown, ChevronUp, Plus, X } from "lucide-react";
import { StudioWritingStyleOptions } from "@/components/studio-writing-style-options";

interface VoiceState {
  label: string;
  description: string;
  dominantTone: string;
  exampleContext: string;
}

interface RecentTopic {
  topic: string;
  format: string;
}

interface StudioAdvancedPanelProps {
  studioAdvancedOpen: boolean;
  setStudioAdvancedOpen: (open: boolean) => void;
  studioAdvancedCount: number;
  advancedEverOpened: boolean;
  setAdvancedEverOpened: (v: boolean) => void;
  request: string;
  voiceStates?: VoiceState[];
  studioVoiceState: string | undefined;
  setStudioVoiceState: (v: string | undefined) => void;
  studioAudienceDescription: string;
  setStudioAudienceDescription: (v: string) => void;
  studioTone: number;
  setStudioTone: (v: number) => void;
  studioPostStyle: string | undefined;
  setStudioPostStyle: (v: string | undefined) => void;
  studioHookStyle: string | undefined;
  setStudioHookStyle: (v: string | undefined) => void;
  studioSpacingStyle: string | undefined;
  setStudioSpacingStyle: (v: string | undefined) => void;
  studioPerspective: string | undefined;
  setStudioPerspective: (v: string | undefined) => void;
  studioHumorLevel: string | undefined;
  setStudioHumorLevel: (v: string | undefined) => void;
  studioAudience: string | undefined;
  setStudioAudience: (v: string | undefined) => void;
  studioEmojiUsage: string | undefined;
  setStudioEmojiUsage: (v: string | undefined) => void;
  studioTargetWordCount: string;
  setStudioTargetWordCount: (v: string) => void;
  studioCustomInstruction: string;
  setStudioCustomInstruction: (v: string) => void;
  studioBlendStyle: string | undefined;
  setStudioBlendStyle: (v: string | undefined) => void;
  studioRecentTopics: RecentTopic[];
  clearStudioContext: () => void;
  clearStudioAdvanced: () => void;
  savePresetInputOpen: boolean;
  setSavePresetInputOpen: (v: boolean) => void;
  savePresetName: string;
  setSavePresetName: (v: string) => void;
  savingPreset: boolean;
  handleSavePreset: () => void;
}

export function StudioAdvancedPanel({
  studioAdvancedOpen,
  setStudioAdvancedOpen,
  studioAdvancedCount,
  advancedEverOpened,
  setAdvancedEverOpened,
  request,
  voiceStates,
  studioVoiceState,
  setStudioVoiceState,
  studioAudienceDescription,
  setStudioAudienceDescription,
  studioTone,
  setStudioTone,
  studioPostStyle,
  setStudioPostStyle,
  studioHookStyle,
  setStudioHookStyle,
  studioSpacingStyle,
  setStudioSpacingStyle,
  studioPerspective,
  setStudioPerspective,
  studioHumorLevel,
  setStudioHumorLevel,
  studioAudience,
  setStudioAudience,
  studioEmojiUsage,
  setStudioEmojiUsage,
  studioTargetWordCount,
  setStudioTargetWordCount,
  studioCustomInstruction,
  setStudioCustomInstruction,
  studioBlendStyle,
  setStudioBlendStyle,
  studioRecentTopics,
  clearStudioContext,
  clearStudioAdvanced,
  savePresetInputOpen,
  setSavePresetInputOpen,
  savePresetName,
  setSavePresetName,
  savingPreset,
  handleSavePreset,
}: StudioAdvancedPanelProps) {
  return (
    <div className="px-4 pt-2 pb-1 border-t border-border/30">
      <div className="relative">
        {!advancedEverOpened && (
          <span className="absolute -top-0.5 -right-0.5 flex h-2 w-2 pointer-events-none">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-500" />
          </span>
        )}
        <button
          onClick={() => {
            setStudioAdvancedOpen(!studioAdvancedOpen);
            if (!advancedEverOpened) {
              setAdvancedEverOpened(true);
              try { localStorage.setItem("studio_advanced_seen", "1"); } catch { /* ignore */ }
            }
          }}
          className="flex items-center gap-2 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors w-full rounded-lg px-2 py-1.5 ring-1 ring-cyan-500/30 shadow-[0_0_8px_0_rgba(6,182,212,0.18)] hover:ring-cyan-500/50 hover:shadow-[0_0_12px_0_rgba(6,182,212,0.28)]"
        >
          <SlidersHorizontal className="w-3.5 h-3.5 text-cyan-500/70" />
          Advanced options
          {studioAdvancedCount > 0 && (
            <span className="ml-1 px-1.5 py-0.5 rounded-full bg-primary/15 text-primary text-[10px] font-bold">
              {studioAdvancedCount}
            </span>
          )}
          {studioAdvancedOpen ? <ChevronUp className="w-3.5 h-3.5 ml-auto" /> : <ChevronDown className="w-3.5 h-3.5 ml-auto" />}
        </button>
      </div>

      <AnimatePresence initial={false}>
        {studioAdvancedOpen && (
          <motion.div
            key="studio-advanced"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="pt-4 space-y-4 pb-2">
              {/* Audience Mirror */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Audience mirror</label>
                  {studioAudienceDescription.trim() && (
                    <button onClick={() => setStudioAudienceDescription("")} className="text-[10px] text-muted-foreground hover:text-foreground underline transition-colors">Clear</button>
                  )}
                </div>
                <input
                  type="text"
                  value={studioAudienceDescription}
                  onChange={(e) => setStudioAudienceDescription(e.target.value.slice(0, 200))}
                  placeholder="Who are you writing for? e.g. startup founders skeptical of AI"
                  className="w-full text-xs bg-background border border-border rounded-xl p-3 text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 transition-all"
                />
                <p className="text-[10px] text-muted-foreground mt-1">AI sharpens emphasis and hooks to resonate with this reader without changing your voice</p>
              </div>

              {/* Voice Mode Selector */}
              {voiceStates && voiceStates.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Voice Mode</label>
                    {studioVoiceState && (
                      <button onClick={() => setStudioVoiceState(undefined)} className="text-[10px] text-muted-foreground hover:text-foreground underline transition-colors">Clear</button>
                    )}
                  </div>
                  <p className="text-[10px] text-muted-foreground/70 mb-2 leading-relaxed">Select which mode of your writing voice to channel in this output.</p>
                  <div className="flex flex-wrap gap-1.5">
                    {voiceStates.map((vs) => (
                      <button
                        key={vs.label}
                        onClick={() => setStudioVoiceState(studioVoiceState === vs.label ? undefined : vs.label)}
                        title={`${vs.description} — Best for: ${vs.exampleContext}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                          studioVoiceState === vs.label
                            ? "bg-primary/15 border-primary/50 text-primary"
                            : "bg-background border-border text-muted-foreground hover:border-primary/30 hover:text-foreground"
                        }`}
                      >
                        {vs.label}
                      </button>
                    ))}
                  </div>
                  {studioVoiceState && (() => {
                    const active = voiceStates.find(s => s.label === studioVoiceState);
                    return active ? (
                      <p className="text-[10px] text-muted-foreground mt-2 pl-1 leading-relaxed italic">{active.description}</p>
                    ) : null;
                  })()}
                </div>
              )}

              {/* Tone slider */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Tone</label>
                  <span className="text-[10px] text-muted-foreground">
                    {studioTone < 30 ? "Casual" : studioTone > 70 ? "Professional" : "Balanced"}
                  </span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={studioTone}
                  onChange={(e) => setStudioTone(Number(e.target.value))}
                  className="w-full accent-primary"
                />
                <div className="flex justify-between text-[10px] text-muted-foreground/50 mt-1">
                  <span>Casual</span>
                  <span>Professional</span>
                </div>
              </div>

              <StudioWritingStyleOptions
                request={request}
                studioPostStyle={studioPostStyle}
                setStudioPostStyle={setStudioPostStyle}
                studioHookStyle={studioHookStyle}
                setStudioHookStyle={setStudioHookStyle}
                studioSpacingStyle={studioSpacingStyle}
                setStudioSpacingStyle={setStudioSpacingStyle}
                studioPerspective={studioPerspective}
                setStudioPerspective={setStudioPerspective}
                studioHumorLevel={studioHumorLevel}
                setStudioHumorLevel={setStudioHumorLevel}
                studioAudience={studioAudience}
                setStudioAudience={setStudioAudience}
                studioEmojiUsage={studioEmojiUsage}
                setStudioEmojiUsage={setStudioEmojiUsage}
              />


              {/* Target Word Count */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Target Word Count</label>
                  {studioTargetWordCount.trim() && (
                    <button onClick={() => setStudioTargetWordCount("")} className="text-[10px] text-muted-foreground hover:text-foreground underline transition-colors">Clear</button>
                  )}
                </div>
                <input
                  type="number"
                  min={5}
                  max={2000}
                  value={studioTargetWordCount}
                  onChange={(e) => setStudioTargetWordCount(e.target.value)}
                  placeholder="e.g. 400"
                  className="w-full text-xs bg-background border border-border rounded-xl p-3 text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 transition-all"
                />
                <p className="text-[10px] text-muted-foreground mt-1">AI will aim for exactly this many words (±10%)</p>
              </div>

              {/* Custom Instruction */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Special instruction</label>
                  {studioCustomInstruction.trim() && (
                    <button onClick={() => setStudioCustomInstruction("")} className="text-[10px] text-muted-foreground hover:text-foreground underline transition-colors">Clear</button>
                  )}
                </div>
                <textarea
                  value={studioCustomInstruction}
                  onChange={(e) => setStudioCustomInstruction(e.target.value.slice(0, 300))}
                  placeholder="e.g. open with a joke, make it 3 sections, end with a quote…"
                  rows={2}
                  className="w-full text-xs bg-background border border-border rounded-xl p-3 text-foreground placeholder:text-muted-foreground/50 resize-none focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 transition-all"
                />
                <p className="text-[10px] text-muted-foreground mt-1 text-right">{studioCustomInstruction.length}/300</p>
              </div>

              {/* Voice Blend */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Blend style</label>
                  {studioBlendStyle && (
                    <button onClick={() => setStudioBlendStyle(undefined)} className="text-[10px] text-muted-foreground hover:text-foreground underline transition-colors">Clear</button>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { id: "ted-talk", label: "TED Talk" },
                    { id: "documentary-narrator", label: "Documentary" },
                    { id: "op-ed-columnist", label: "Op-ed" },
                    { id: "mentor-letter", label: "Mentor letter" },
                    { id: "investigative-journalist", label: "Investigative" },
                    { id: "comedian-storyteller", label: "Stand-up story" },
                    { id: "startup-founder-post", label: "Founder's story" },
                    { id: "coach-framework", label: "Coach's framework" },
                    { id: "confessional-diary", label: "Confessional" },
                    { id: "poet-prose", label: "Prose poet" },
                  ].map((archetype) => (
                    <button
                      key={archetype.id}
                      onClick={() => setStudioBlendStyle(studioBlendStyle === archetype.id ? undefined : archetype.id)}
                      className={`px-2.5 py-1 text-[11px] rounded-lg border transition-all font-medium ${
                        studioBlendStyle === archetype.id
                          ? "bg-primary/10 border-primary/40 text-primary"
                          : "bg-background border-border text-muted-foreground hover:text-foreground hover:border-border/80"
                      }`}
                    >
                      {archetype.label}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">Borrows structural architecture from this format while keeping your vocabulary and cadence</p>
              </div>

              {/* Recent Context */}
              {studioRecentTopics.length > 0 && (
                <div className="pt-1">
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] text-muted-foreground/70">
                      <span className="font-semibold text-muted-foreground">Context:</span>{" "}
                      {studioRecentTopics[0].topic.length > 40
                        ? studioRecentTopics[0].topic.slice(0, 40) + "…"
                        : studioRecentTopics[0].topic}
                      {studioRecentTopics.length > 1 && ` +${studioRecentTopics.length - 1} more`}
                    </p>
                    <button
                      onClick={clearStudioContext}
                      className="text-[10px] text-muted-foreground hover:text-foreground underline transition-colors"
                    >
                      Clear context
                    </button>
                  </div>
                </div>
              )}

              {/* Save as Preset */}
              <div className="flex items-center justify-between pt-1 border-t border-border/20">
                <AnimatePresence mode="wait">
                  {savePresetInputOpen ? (
                    <motion.div
                      key="preset-input"
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      className="flex items-center gap-1.5 flex-1"
                    >
                      <input
                        autoFocus
                        type="text"
                        value={savePresetName}
                        onChange={(e) => setSavePresetName(e.target.value.slice(0, 60))}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleSavePreset();
                          if (e.key === "Escape") setSavePresetInputOpen(false);
                        }}
                        placeholder="Name this preset..."
                        className="flex-1 text-xs bg-background border border-border rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 transition-all"
                      />
                      <button
                        onClick={handleSavePreset}
                        disabled={!savePresetName.trim() || savingPreset}
                        className="px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-primary/15 text-primary hover:bg-primary/25 transition-colors disabled:opacity-40"
                      >
                        {savingPreset ? "Saving..." : "Save"}
                      </button>
                      <button
                        onClick={() => setSavePresetInputOpen(false)}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </motion.div>
                  ) : (
                    <motion.button
                      key="preset-btn"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      type="button"
                      onClick={() => { setSavePresetName(""); setSavePresetInputOpen(true); }}
                      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
                    >
                      <Plus className="w-3 h-3" />
                      Save as preset
                    </motion.button>
                  )}
                </AnimatePresence>
                {studioAdvancedCount > 0 && !savePresetInputOpen && (
                  <button
                    onClick={clearStudioAdvanced}
                    className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors ml-3"
                  >
                    Clear all
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
